/**
 * Feedback Modal Component
 *
 * Modal dialog for collecting user feedback and defect reports.
 * Submits to Adobe APS Portal metrics backend.
 */

import React, { useState } from 'react';
import { submitFeedback } from '../services/sessionAPI';
import './FeedbackModal.css';

const FeedbackModal = ({ isOpen, onClose, userEmail }) => {
  const [feedbackType, setFeedbackType] = useState('feedback');
  const [customerName, setCustomerName] = useState('');
  const [hoursSaved, setHoursSaved] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success' or 'error'

  // Calculate word count
  const wordCount = comments.trim().split(/\s+/).filter(word => word.length > 0).length;
  const maxWords = 100;

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setFeedbackType('feedback');
      setCustomerName('');
      setHoursSaved('');
      setComments('');
      setError('');
    }
  }, [isOpen]);

  const showNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleCommentsChange = (e) => {
    const text = e.target.value;
    const words = text.trim().split(/\s+/).filter(word => word.length > 0);

    if (words.length <= maxWords) {
      setComments(text);
    } else {
      // Truncate to max words
      const truncated = words.slice(0, maxWords).join(' ');
      setComments(truncated);
    }
  };

  const validateForm = () => {
    if (!customerName.trim()) {
      setError('Customer name is required');
      return false;
    }

    if (feedbackType === 'feedback') {
      if (!hoursSaved || hoursSaved.trim() === '') {
        setError('Hours saved is required for feedback');
        return false;
      }

      const hours = parseFloat(hoursSaved);
      if (isNaN(hours) || hours <= 0) {
        setError('Hours saved must be a positive number');
        return false;
      }
    }

    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        email: userEmail,
        customerName: customerName.trim(),
        type: feedbackType,
      };

      if (feedbackType === 'feedback' && hoursSaved) {
        payload.hoursSaved = parseFloat(hoursSaved);
      }

      if (comments.trim()) {
        payload.comments = comments.trim();
      }

      await submitFeedback(userEmail, payload);

      showNotification('Thank you for your feedback!', 'success');
      onClose();
    } catch (err) {
      const errorMessage = err.message || 'Failed to submit feedback. Please try again.';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="feedback-modal-overlay" onClick={onClose}>
        <div className="feedback-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="feedback-modal-header">
            <h2>Share Your Feedback</h2>
            <button className="feedback-modal-close" onClick={onClose} aria-label="Close">
              &times;
            </button>
          </div>

          <form onSubmit={handleSubmit} className="feedback-modal-form">
            {/* Feedback Type */}
            <div className="feedback-form-group">
              <label htmlFor="feedbackType">Type</label>
              <select
                id="feedbackType"
                value={feedbackType}
                onChange={(e) => setFeedbackType(e.target.value)}
                disabled={submitting}
              >
                <option value="feedback">Feedback</option>
                <option value="defect">Defect</option>
              </select>
            </div>

            {/* Email (read-only) */}
            <div className="feedback-form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={userEmail}
                readOnly
                disabled
                className="feedback-readonly-input"
              />
            </div>

            {/* Customer Name */}
            <div className="feedback-form-group">
              <label htmlFor="customerName">
                Customer Name <span className="feedback-required">*</span>
              </label>
              <input
                id="customerName"
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Enter customer name"
                disabled={submitting}
                required
              />
            </div>

            {/* Hours Saved (only for feedback type) */}
            {feedbackType === 'feedback' && (
              <div className="feedback-form-group">
                <label htmlFor="hoursSaved">
                  Hours Saved <span className="feedback-required">*</span>
                </label>
                <input
                  id="hoursSaved"
                  type="number"
                  step="0.5"
                  min="0"
                  value={hoursSaved}
                  onChange={(e) => setHoursSaved(e.target.value)}
                  placeholder="e.g., 5.5"
                  disabled={submitting}
                  required
                />
              </div>
            )}

            {/* Jira Link Prompt for Defects */}
            {feedbackType === 'defect' && (
              <div className="feedback-info-box">
                <strong>Note:</strong> Please create a Jira ticket for this defect and include
                relevant details in the comments below.
              </div>
            )}

            {/* Comments */}
            <div className="feedback-form-group">
              <label htmlFor="comments">
                Comments (Optional)
                <span className="feedback-word-count">
                  {wordCount}/{maxWords} words
                </span>
              </label>
              <textarea
                id="comments"
                value={comments}
                onChange={handleCommentsChange}
                placeholder="Share your thoughts..."
                rows="4"
                disabled={submitting}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="feedback-error-message">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="feedback-modal-actions">
              <button
                type="button"
                onClick={onClose}
                className="feedback-btn feedback-btn-cancel"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="feedback-btn feedback-btn-submit"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div className={`feedback-toast feedback-toast-${toastType}`}>
          {toastMessage}
        </div>
      )}
    </>
  );
};

export default FeedbackModal;
